import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CompanyPermissionsConfigService } from '../../services/company-permissions-config.service';
import { COMPANY_ACTION } from '../companies/companies.constants';

/**
 * Lifecycle action buttons for the company detail view (PRD HU5, catalog of actions).
 *
 * Visibility is driven by company-permissions.config.json — each button is shown only if the
 * config allows the action for the current status AND the user has the corresponding permission
 * role. Mirrors PoApprovalActionsComponent; the parent handles the actual API calls and only
 * receives the emitted event on click.
 */
@Component({
  selector: 'app-company-approval-actions',
  templateUrl: './company-approval-actions.component.html',
  styleUrls: ['./company-approval-actions.component.scss'],
})
export class CompanyApprovalActionsComponent {
  @Input() statusCode: string | null = null;

  @Output() submitForVerification = new EventEmitter<void>();
  @Output() retryComplianceValidation = new EventEmitter<void>();
  @Output() forceFinancialValidation = new EventEmitter<void>();
  @Output() verify = new EventEmitter<void>();
  @Output() returnToKam = new EventEmitter<void>();
  @Output() forceVerification = new EventEmitter<void>();

  constructor(
    private readonly permissionsConfig: CompanyPermissionsConfigService,
  ) {}

  get canSubmit(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.SUBMIT_FOR_VERIFICATION);
  }

  get canRetryCompliance(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.RETRY_COMPLIANCE_VALIDATION);
  }

  get canForceFinancialValidation(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.FORCE_FINANCIAL_VALIDATION);
  }

  get canVerify(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.VERIFY);
  }

  get canReturnToKam(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.RETURN_TO_KAM);
  }

  get canForceVerification(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.FORCE_VERIFICATION);
  }

  get hasAnyAction(): boolean {
    return this.canSubmit
      || this.canRetryCompliance
      || this.canForceFinancialValidation
      || this.canVerify
      || this.canReturnToKam
      || this.canForceVerification;
  }
}
